const _excluded = ["className", "variant", "weight", "skin", "capitalize", "as", "children", "style", "textDecoration", "noWrap", "breakWords", "showWhiteSpaces"],
  _excluded2 = ["className", "children", "ellipsis", "tooltipProps", "variant", "skin", "weight", "hideTooltip", "tooltipTestId", "onChange", "onFocus", "onBlur"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useRef, useState, useMemo } from 'react';
import cn from 'classnames';
import { Tooltip } from "../Tooltip";
import styles from "./Typography.module.scss";
import { jsx as _jsx } from "react/jsx-runtime";
/** Renders text with configurable presets. */
const BaseTypography = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      className,
      variant,
      weight,
      skin,
      capitalize,
      as,
      children,
      style,
      textDecoration,
      noWrap,
      breakWords,
      showWhiteSpaces
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const Component = as || 'div';
  const renderedStyle = useMemo(() => style ? _objectSpread(_objectSpread({}, style), {}, {
    textDecoration
  }) : {
    textDecoration
  }, [style, textDecoration]);
  return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({
    style: renderedStyle,
    ref: ref,
    className: cn(styles.root, styles["".concat(variant, "Variant")], styles["".concat(weight, "Weight")], styles["".concat(skin, "Skin")], {
      [styles.capitalize]: capitalize,
      [styles.noWrap]: noWrap,
      [styles.breakWords]: breakWords,
      [styles.showWhiteSpaces]: showWhiteSpaces
    }, className)
  }, rest), {}, {
    children: children
  }));
});
BaseTypography.defaultProps = {
  variant: 'paragraph1',
  skin: 'standard',
  weight: 'light'
};
BaseTypography.displayName = 'BaseTypography';
/**
 * Renders a Typography component, limited to one-line of text, with an ellipsis
 * if the text is overflowing; wrapped in a tooltip. Will show the full text in the
 * tooltip only if text is overflowing.
 */
export const Typography = /*#__PURE__*/React.forwardRef(function Typography(props, _ref) {
  const {
      className,
      children,
      ellipsis,
      tooltipProps,
      variant = 'paragraph1',
      skin = 'standard',
      weight = 'light',
      hideTooltip,
      tooltipTestId,
      onChange: _onChange,
      onFocus: _onFocus,
      onBlur: _onBlur
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  const textRef = useRef(null);
  const resizeObsRef = useRef();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  let title = '';
  if (!isFocused && isOverflowing && textRef.current) {
    if (tooltipProps && tooltipProps.title) {
      title = tooltipProps.title;
    } else if (typeof children === 'string') {
      title = children;
    } else if (textRef.current.textContent) {
      title = textRef.current.textContent;
    } else if (textRef.current.value) {
      title = textRef.current.value;
    }
  }
  return ellipsis && !hideTooltip ? /*#__PURE__*/_jsx(Tooltip, _objectSpread(_objectSpread({
    placement: "top"
  }, tooltipProps), {}, {
    title: tooltipTestId && title ? /*#__PURE__*/_jsx("div", {
      "data-testid": tooltipTestId,
      children: title
    }) : title,
    children: /*#__PURE__*/_jsx(BaseTypography, _objectSpread(_objectSpread({}, rest), {}, {
      variant: variant,
      skin: skin,
      weight: weight,
      ref: el => {
        if (ellipsis) {
          if (el) {
            // Use ResizeObserver to detect changes in element size and render tooltip accordingly.
            if (!resizeObsRef.current && 'ResizeObserver' in window) {
              resizeObsRef.current = new ResizeObserver(entries => {
                requestAnimationFrame(() => {
                  if (!Array.isArray(entries) || !entries.length) return;
                  for (const entry of entries) {
                    entry.target && setIsOverflowing(entry.target.clientWidth < entry.target.scrollWidth);
                  }
                });
              });
            }
            resizeObsRef.current && resizeObsRef.current.observe(el);
          } else if (resizeObsRef.current && textRef.current) {
            resizeObsRef.current.unobserve(textRef.current);
          }
        }
        textRef.current = el;
        if (typeof _ref === 'function') {
          _ref(el);
        } else if (_ref) {
          _ref.current = el;
        }
      },
      onChange: e => {
        // This is important for usage with as="input".
        setIsOverflowing(e.currentTarget.clientWidth < e.currentTarget.scrollWidth);
        _onChange && _onChange(e);
      },
      onFocus: e => {
        // This is important for usage with as="input" to hide tooltip when focused.
        setIsFocused(true);
        _onFocus && _onFocus(e);
      },
      onBlur: e => {
        // This is important for usage with as="input" to hide tooltip when focused.
        setIsFocused(false);
        _onBlur && _onBlur(e);
      },
      className: cn(styles.ellipsis, className),
      children: children
    }))
  })) : /*#__PURE__*/_jsx(BaseTypography, _objectSpread(_objectSpread({}, rest), {}, {
    variant: variant,
    skin: skin,
    weight: weight,
    ref: _ref,
    className: cn(className, {
      [styles.ellipsis]: ellipsis
    }),
    onChange: _onChange,
    onFocus: _onFocus,
    onBlur: _onBlur,
    children: children
  }));
});